import React, { useEffect, useState } from 'react';
import { SummarizerConfig } from '../../types/summarizer';
import { useConfigStore } from '../../core/store/useConfigStore';
import { useSummarizer } from '../../core/store/useSummarizer';
import { UniversalSummarizer } from '../summarizer/universal';
import { getToolById } from '../../config/summarizers';


interface ConfigLoaderProps {
  toolType: string;
  configMode?: 'default' | 'override' | 'custom';
  configOverride?: Partial<SummarizerConfig>;
  className?: string;
  customCSS?: string;
}

/**
 * ConfigLoader Component
 *
 * Loads configuration from database or uses defaults,
 * applies overrides, and renders the UniversalSummarizer
 * with the final configuration
 */
export const ConfigLoader: React.FC<ConfigLoaderProps> = ({
  toolType,
  configMode = 'default',
  configOverride,
  className,
  customCSS,
}) => {
  const [finalConfig, setFinalConfig] = useState<SummarizerConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { loadConfig, configs, applyOverrides, setOverride, clearOverride } = useConfigStore();

  const { setCurrentConfig } = useSummarizer();


  useEffect(() => {
    const loadConfiguration = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Load base configuration
        let baseConfig = configs[toolType];
        if (!baseConfig) {
          // Try to load from API if not in store
          const loadedConfig = await loadConfig(toolType);

          if (!loadedConfig) {
            // If API fails, try to get default config from local registry
            const defaultConfig = getToolById(toolType);

            if (defaultConfig) {
              baseConfig = defaultConfig;
            } else {
              throw new Error(
                `Configuration for tool "${toolType}" not found in API or local registry`
              );
            }
          } else {
            baseConfig = loadedConfig;
          }
        } else {
        }

        let resultConfig: SummarizerConfig = baseConfig;

        // Apply overrides based on config mode
        if (configMode === 'override' && configOverride) {
          // Store override in Zustand for persistence
          const ui = baseConfig.ui;
          setOverride(toolType, {
            ...configOverride,
            ui: {
              ...ui,
              ...configOverride.ui,
            },
          });
          resultConfig = applyOverrides(baseConfig, {
            ...configOverride,
            ui: {
              ...ui,
              ...configOverride.ui,
            },
          });
        } else if (configMode === 'custom' && configOverride) {
          // Full custom config replacement
          const ui = baseConfig.ui;
          resultConfig = {
            ...baseConfig,
            ...configOverride,
            ui: {
              ...ui,
              ...configOverride.ui,
            },
            id: toolType, // Ensure ID stays consistent
          } as SummarizerConfig;
        } else if (configMode === 'default') {
          // Clear any existing overrides
          clearOverride(toolType);
        }

        // Save the final configuration to both local state and global store
        setFinalConfig(resultConfig);
        setCurrentConfig(resultConfig);
      } catch (err) {
        console.error('Failed to load configuration:', err);
        setError(err instanceof Error ? err.message : 'Failed to load configuration');
        // Clear config in store on error
        setCurrentConfig(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfiguration();
  }, [toolType, configMode, configOverride, setCurrentConfig]);

  // Apply custom CSS if provided
  useEffect(() => {
    if (customCSS) {
      const styleId = `tubeonai-custom-style-${toolType}`;
      let styleElement = document.getElementById(styleId) as HTMLStyleElement;

      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }

      styleElement.textContent = customCSS;

      // Cleanup on unmount
      return () => {
        if (styleElement && styleElement.parentNode) {
          styleElement.parentNode.removeChild(styleElement);
        }
      };
    }
    // Return undefined when no customCSS
    return undefined;
  }, [customCSS, toolType]);

  if (isLoading) {
    return (
      <div className={`tubeonai-config-loader ${className || ''}`}>
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <span className="ml-3">Loading configuration...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`tubeonai-config-loader ${className || ''}`}>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!finalConfig) {
    return (
      <div className={`tubeonai-config-loader ${className || ''}`}>
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          No configuration available for this tool.
        </div>
      </div>
    );
  }

  
  return (
    <div className={`tubeonai-config-loader ${className || ''}`}>
      <UniversalSummarizer config={finalConfig} className={className} />
    </div>
  );
};

export default ConfigLoader;
